import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { SESSION_PROPERTY_KEYS } from "@thkStore/Constants/Session/sessionPropertyKeys";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    companyName: state.components.contactUsForm.fieldValues["companyName"],
    sessionCompanyName: state.context.session?.properties?.[SESSION_PROPERTY_KEYS.company],
    isAuthenticated: state.context.session?.isAuthenticated,
});

const mapDispatchToProps = { setFieldValue };

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {};

const styles = companyNameStyles;

const CompanyName: React.FC<Props> = ({
    fields, companyName, sessionCompanyName, isAuthenticated, setFieldValue,
}) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (isAuthenticated && sessionCompanyName && !companyName) {
            setFieldValue({ key: "companyName", value: sessionCompanyName });
        }
    }, [isAuthenticated, sessionCompanyName]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validate;
        if (typeof companyName !== "undefined") validate();
    }, [companyName, isRequired]);

    const validate = () => {
        const error = isRequired && !companyName
            ? (siteMessage("Field_Required", label) as string)
            : "";
        setErrorMessage(error);
        return !error;
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={companyName || ""}
            error={errorMessage}
            onChange={e => setFieldValue({ key: "companyName", value: e.target.value })}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            { name: fields.label, displayName: "Label", editorTemplate: "TextField", fieldType: "Translatable", defaultValue: "Company Name", isRequired: true },
            { name: fields.hintText, displayName: "Hint Text", editorTemplate: "TextField", fieldType: "Translatable", defaultValue: "Enter Company Name", isRequired: true },
            { name: fields.isRequired, displayName: "Required Field", editorTemplate: "CheckboxField", defaultValue: true, fieldType: "General" },
        ],
    },
};

export default widgetModule;
